"use client"

import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import ReactMarkdown from "react-markdown"
import { MessageSquare, X, Send, Bot, User, Loader2, Volume2, Square } from "lucide-react"

interface Message {
    role: "user" | "assistant"
    content: string
    timestamp?: Date
}

export default function ChatWidget() {
    const [isOpen, setIsOpen] = useState(false)
    const [messages, setMessages] = useState<Message[]>([
        { role: "assistant", content: "Hi there! I'm Rakesh's AI assistant. Ask me anything about his expertise in Agentic AI or his guiding principles!", timestamp: new Date() },
    ])
    const [inputValue, setInputValue] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [speakingMessageId, setSpeakingMessageId] = useState<number | null>(null)
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const lastSpokenMessageIndex = useRef<number>(0) // Initialize to 0 to skip the first welcome message
    const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
    const [isMounted, setIsMounted] = useState(false)

    useEffect(() => {
        setIsMounted(true)
    }, [])

    useEffect(() => {
        const loadVoices = () => {
            const availableVoices = window.speechSynthesis.getVoices()
            setVoices(availableVoices)
        }

        loadVoices()
        window.speechSynthesis.onvoiceschanged = loadVoices

        return () => {
            window.speechSynthesis.onvoiceschanged = null
        }
    }, [])

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }

    useEffect(() => {
        scrollToBottom()

        // Auto-play the last message if it's from the assistant and hasn't been spoken yet
        const lastIndex = messages.length - 1
        if (lastIndex > lastSpokenMessageIndex.current) {
            const lastMessage = messages[lastIndex]
            if (lastMessage.role === "assistant") {
                speakMessage(lastMessage.content, lastIndex)
                lastSpokenMessageIndex.current = lastIndex
            }
        }
    }, [messages])

    useEffect(() => {
        return () => {
            window.speechSynthesis.cancel()
        }
    }, [])

    const speakMessage = (text: string, index: number) => {
        if (speakingMessageId === index) {
            window.speechSynthesis.cancel()
            setSpeakingMessageId(null)
            return
        }

        window.speechSynthesis.cancel()
        const utterance = new SpeechSynthesisUtterance(text)

        // Optional: Select a voice (preferably a natural English voice)
        // Optional: Select a voice (preferably a natural English voice)
        // Prioritize Microsoft Zira (common Windows female voice) or Google US English
        const preferredVoice = voices.find(voice =>
            voice.name.includes("Zira") ||
            voice.name.includes("Female") || // Generic check for female
            voice.name.includes("Google US English") ||
            (voice.lang.startsWith("en") && voice.name.includes("Microsoft")) // Fallback to other Microsoft voices which are often high quality
        ) || voices.find(voice => voice.lang.startsWith("en"))

        if (preferredVoice) utterance.voice = preferredVoice

        utterance.onend = () => setSpeakingMessageId(null)
        utterance.onerror = () => setSpeakingMessageId(null)

        window.speechSynthesis.speak(utterance)
        setSpeakingMessageId(index)
    }

    const handleSend = async () => {
        if (!inputValue.trim() || isLoading) return

        const userMessage: Message = { role: "user", content: inputValue, timestamp: new Date() }
        setMessages((prev) => [...prev, userMessage])
        setInputValue("")
        setIsLoading(true)

        try {
            const response = await fetch("/api/chat", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ messages: [...messages, userMessage] }),
            })

            const data = await response.json()

            // Handle rate limiting
            if (response.status === 429) {
                setMessages((prev) => [...prev, {
                    role: "assistant",
                    content: `⏳ ${data.error || "You're sending messages too quickly. Please wait a moment before trying again."}`,
                    timestamp: new Date()
                }])
                return
            }

            // Handle service unavailable
            if (response.status === 503) {
                setMessages((prev) => [...prev, {
                    role: "assistant",
                    content: "I'm temporarily offline for maintenance. In the meantime, feel free to reach out through the [Contact page](/#contact) — Rakesh would love to hear from you!",
                    timestamp: new Date()
                }])
                return
            }

            if (data.content) {
                setMessages((prev) => [...prev, { role: "assistant", content: data.content, timestamp: new Date() }])
            } else {
                throw new Error("Invalid response")
            }
        } catch (error) {
            console.error("Chat error:", error)
            setMessages((prev) => [...prev, { role: "assistant", content: "😔 Sorry, I'm having trouble responding right now. You can try again in a moment, or feel free to connect with Rakesh directly through the [Contact page](/#contact)!", timestamp: new Date() }])
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="fixed bottom-6 right-6 z-[9999]">
            <AnimatePresence>
                {!isOpen && (
                    <motion.button
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        onClick={() => setIsOpen(true)}
                        className="absolute bottom-0 right-0 w-16 h-16 rounded-full bg-[#163A98]/20 backdrop-blur-xl border border-white/20 text-white flex items-center justify-center shadow-[0_0_30px_rgba(22,58,152,0.4)] hover:bg-[#163A98]/40 hover:border-white/40 transition-all duration-300 group"
                    >
                        <MessageSquare className="w-8 h-8 group-hover:scale-110 transition-transform text-white/90" />

                        {/* Pulsing Outer Ring */}
                        <div className="absolute inset-0 rounded-full border border-white/10 animate-ping opacity-20 pointer-events-none" />
                    </motion.button>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ y: 20, opacity: 0, scale: 0.95, originX: 1, originY: 1 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 20, opacity: 0, scale: 0.95 }}
                        className="absolute bottom-0 right-0 w-[350px] sm:w-[400px] h-[600px] bg-[#020617]/95 backdrop-blur-3xl border border-white/10 rounded-[28px] shadow-2xl flex flex-col overflow-hidden origin-bottom-right"
                    >
                        {/* Header - Matching Core Expertise Gradients */}
                        <div className="p-4 bg-gradient-to-r from-[#163A98] to-[#020617] text-white flex items-center justify-between border-b border-white/10">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-lg">
                                    <Bot className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-sm tracking-tight font-[family-name:var(--font-plus-jakarta-sans)]">Rakesh's AI Assistant</h3>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                                        <span className="text-[9px] uppercase tracking-widest font-bold text-blue-300/80">Online</span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors border border-transparent hover:border-white/10"
                            >
                                <X className="w-4 h-4 opacity-70 hover:opacity-100" />
                            </button>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-5 space-y-5 scrollbar-thin scrollbar-thumb-white/10 scroll-smooth">
                            {messages.map((msg, i) => (
                                <div
                                    key={i}
                                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                                >
                                    <div
                                        className={`max-w-[85%] p-4 rounded-2xl text-[15px] leading-relaxed font-[family-name:var(--font-plus-jakarta-sans)] ${msg.role === "user"
                                            ? "bg-[#163A98] text-white rounded-tr-none shadow-lg border border-white/10"
                                            : "bg-white/5 text-white/90 border border-white/10 rounded-tl-none backdrop-blur-sm"
                                            }`}
                                    >
                                        <div className="prose prose-invert prose-sm max-w-none text-white break-words [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4 [&>strong]:text-blue-200 [&>code]:bg-white/10 [&>code]:px-1 [&>code]:py-0.5 [&>code]:rounded [&>pre]:bg-black/30 [&>pre]:p-2 [&>pre]:rounded-lg">
                                            <ReactMarkdown>
                                                {msg.content}
                                            </ReactMarkdown>
                                        </div>
                                        {msg.timestamp && (
                                            <div className={`text-[10px] mt-2 opacity-60 flex items-center justify-between ${msg.role === "user" ? "text-blue-100" : "text-gray-400"}`}>
                                                <span suppressHydrationWarning>{isMounted && msg.timestamp ? msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}</span>
                                                {msg.role === "assistant" && (
                                                    <button
                                                        onClick={() => speakMessage(msg.content, i)}
                                                        className="p-1 hover:bg-white/10 rounded-full transition-colors ml-2"
                                                        title={speakingMessageId === i ? "Stop speaking" : "Read aloud"}
                                                    >
                                                        {speakingMessageId === i ? (
                                                            <Square className="w-3 h-3 text-blue-400 animate-pulse" fill="currentColor" />
                                                        ) : (
                                                            <Volume2 className="w-3 h-3 opacity-70 hover:opacity-100" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {isLoading && (
                                <div className="flex justify-start">
                                    <div className="bg-white/5 text-white/60 border border-white/10 p-4 rounded-2xl rounded-tl-none flex items-center gap-3">
                                        <div className="flex gap-1">
                                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" />
                                        </div>
                                        <span className="text-xs font-medium tracking-wide">Processing...</span>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input - More Premium Glass Look */}
                        <div className="p-4 border-t border-white/10 bg-white/[0.02]">
                            <div className="relative group">
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyPress={(e) => e.key === "Enter" && handleSend()}
                                    placeholder="Ask about AI Strategy or Agents..."
                                    className="w-full bg-white/5 border border-white/10 rounded-[18px] py-3 px-5 pr-14 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#2563EB]/50 focus:bg-white/[0.08] transition-all duration-300"
                                />
                                <button
                                    onClick={handleSend}
                                    disabled={isLoading || !inputValue.trim()}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 text-blue-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300 rounded-xl hover:bg-[#2563EB]/20"
                                >
                                    <Send className="w-5 h-5" />
                                </button>
                            </div>
                            <p className="text-[10px] text-white/20 mt-2 text-center font-medium uppercase tracking-widest">
                                Powered by Agentic Architectures
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
